import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabaseClient } from '../../../shared/utils/supabaseClient';

const ContactForm = () => {
  const [form, setForm] = useState({ name: '', email: '', description: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      description: form.description.trim()
    };
    console.log('[ContactForm] Submitting to support_requests:', payload);
    try {
      const { data, error: dbError } = await supabaseClient
        .from('support_requests')
        .insert(payload)
        .select()
        .single();
      console.log('[ContactForm] Insert response — data:', data, 'error:', dbError);
      if (dbError) throw dbError;
      if (!data) throw new Error('Insert was silently blocked — check RLS policy on support_requests table');
      setSubmitted(true);
    } catch (err) {
      console.error('[ContactForm] Error submitting support request:', err);
      setError('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-4">
        <div className="w-12 h-12 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <svg className="w-6 h-6 text-success-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p className="font-medium text-neutral-900">Your message has been sent.</p>
        <p className="text-sm text-neutral-500 mt-1">We will respond within 24 hours.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-1">Name</label>
        <input
          type="text"
          name="name"
          value={form.name}
          onChange={handleChange}
          required
          className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="Your name"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-1">Email</label>
        <input
          type="email"
          name="email"
          value={form.email}
          onChange={handleChange}
          required
          className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="your@email.com"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-1">Issue Description</label>
        <textarea
          name="description"
          value={form.description}
          onChange={handleChange}
          required
          rows={4}
          className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="Describe your issue..."
        />
      </div>
      {error && <p className="text-error-600 text-sm">{error}</p>}
      <button
        type="submit"
        disabled={loading}
        className="w-full px-4 py-2 bg-primary-600 text-neutral-50 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
      >
        {loading ? 'Sending...' : 'Send Message'}
      </button>
    </form>
  );
};

const FAQPage = () => {
  const navigate = useNavigate();
  const [expandedItem, setExpandedItem] = useState(null);

  useEffect(() => {
    // Log FAQ view
    const logFAQView = async () => {
      try {
        const { data: { user } } = await supabaseClient.auth.getUser();
        if (user) {
          await supabaseClient.from('audit_logs').insert({
            event_category: 'support',
            event_type: 'faq_viewed',
            actor_id: user.id,
            actor_role: 'user',
            timestamp: new Date().toISOString()
          });
        }
      } catch (err) {
        console.error('Error logging FAQ view:', err);
      }
    };
    logFAQView();
  }, []);

  // Two labeled groups on the single /faq page. Backtick strings so UI-label
  // double-quotes and apostrophes in the copy need no escaping.
  const MEMBER_FAQS = [
    {
      question: `What is EsuSol?`,
      answer: `EsuSol helps your savings group run smoothly. It tracks who has contributed, whose turn it is to collect, and keeps a clear, timestamped record for everyone. EsuSol does not hold or move money. All payments happen directly between members.`
    },
    {
      question: `What is a hand?`,
      answer: `A hand is one position in the rotation. You can hold more than one hand, which means you contribute and collect more than once per cycle.`
    },
    {
      question: `It is my turn to collect. Do I still contribute this round?`,
      answer: `No. In the round you collect, you skip your contribution and receive the pot from the other members. You contribute again in every other round.`
    },
    {
      question: `Why does my dashboard say "Collecting" instead of showing a payment due?`,
      answer: `Because it is your turn to collect this round. You owe no contribution now. You will contribute again in your next non-collecting round.`
    },
    {
      question: `How do I mark my contribution as paid?`,
      answer: `Send your payment to the collector directly using your group's method, then tap "I Paid" on your dashboard so the admin can confirm it.`
    },
    {
      question: `How do I confirm I received my payout?`,
      answer: `When it is your turn to collect and the payout is sent, a payout banner appears on your dashboard. Tap "Confirm Receipt" once you have received the funds.`
    },
    {
      question: `What happens if I pay late?`,
      answer: `Your status shows as pending until the admin confirms your payment. Your group may charge a late fee, which is separate from your contribution.`
    },
    {
      question: `Do I keep contributing after I collect?`,
      answer: `Yes. Collecting your payout does not end your commitment. You continue contributing every round until the cycle is complete, so every member gets their full turn.`
    },
    {
      question: `Can I use my EsuSol records for financial or mortgage documentation?`,
      answer: `We are developing this. EsuSol keeps a timestamped, tamper-evident record of your group's activity, including your contributions, payouts, and the agreements you accepted. We are working toward a documented history members can use to support financial documentation, such as savings history for lenders. This feature is still in development, and any such record reflects what was logged in EsuSol, not a guarantee that funds moved. We will share more as it becomes available.`
    },
    {
      question: `Does EsuSol hold or process my money?`,
      answer: `No. EsuSol never holds, moves, or processes money. All payments happen directly between members. EsuSol is a record-keeping tool only.`
    },
    {
      question: `Is my information private?`,
      answer: `Your name, rotation position, and payment status are visible to your group admin and members, because a savings circle depends on shared visibility. Your information is never sold or shared for marketing. See our Privacy Policy for details.`
    }
  ];

  const ADMIN_FAQS = [
    {
      question: `How do I add members to my group?`,
      answer: `Open your group and click Add Member, then enter a display name. Once added, an invite link appears for that specific member. Share their link with them by WhatsApp or copy it. They open it to finalize their own onboarding, where they confirm their legal name, verify their phone number, and accept the group agreement.`
    },
    {
      question: `What is the difference between adding a member and them onboarding?`,
      answer: `You add each member yourself with a display name, and EsuSol generates an invite link for that specific person. When they open their link, they confirm their legal name, verify their phone, and accept the agreement. You control who is in the group; each member completes their own details and consent.`
    },
    {
      question: `How do I confirm a member's payment?`,
      answer: `Open the group, click the member's hand row, enter the payment details, and submit. The record is timestamped for the group's history.`
    },
    {
      question: `What does "Covered by Admin" mean?`,
      answer: `It means you paid on that member's behalf so the round could stay on schedule. The member owes you that amount, and the record is kept in the group's history.`
    },
    {
      question: `How do I send a payout to the collector?`,
      answer: `Once the other members have paid their contributions, click Send Payout on the collector's hand row and enter the payout details. The collector does not contribute in their own collection round, so you do not wait for their payment.`
    },
    {
      question: `Why is there no "Record Payment" for the collector?`,
      answer: `Because the collector does not contribute in the round they collect. Their row shows "Collecting" instead, and the Send Payout button appears once the other members have paid.`
    },
    {
      question: `How much does the collector receive?`,
      answer: `The collector receives the total of the other members' contributions for that round. Their own contribution is not part of their collection round.`
    },
    {
      question: `How do I add a late fee?`,
      answer: `When a member is past their due date and still unpaid, an "Add Late Fee" option appears on their row. Late fees are tracked separately from contributions, giving you a record of who has fallen behind to decide how to handle a member going forward.`
    },
    {
      question: `Can I change the rotation order?`,
      answer: `Yes. Go to View Rotation and click Edit Rotation. If payouts have already been sent, a warning appears, because changing the order mid-cycle affects who has and has not collected.`
    },
    {
      question: `How does a cycle end?`,
      answer: `A cycle completes when every hand has collected once. Members contribute in every round except their own collection round, so the pot rotates fully through the group.`
    },
    {
      question: `How do I export payment history?`,
      answer: `Data export is coming in a future update.`
    },
    {
      question: `Does EsuSol hold or process money?`,
      answer: `No. EsuSol never holds, moves, or processes money. All payments happen directly between members via Zelle, Cash App, Venmo, or other methods. EsuSol is a record-keeping tool only.`
    }
  ];

  // One accordion item, keyed by a section-scoped string so the two sections
  // never collide on expandedItem. Same markup/styling as before.
  const renderFaqItem = (faq, key) => (
    <div key={key} className="bg-neutral-100 rounded-lg shadow-sm border border-neutral-200">
      <button
        onClick={() => setExpandedItem(expandedItem === key ? null : key)}
        className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-neutral-50"
      >
        <span className="font-medium text-neutral-900">{faq.question}</span>
        <svg
          className={`w-5 h-5 text-neutral-500 transition-transform flex-shrink-0 ml-3 ${
            expandedItem === key ? 'transform rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {expandedItem === key && (
        <div className="px-6 pb-4 pt-0">
          <p className="text-neutral-600">{faq.answer}</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="text-neutral-600 hover:text-neutral-900 mb-4 inline-block"
          >
            ← Back
          </button>
          <h1 className="text-3xl font-bold text-neutral-900 mb-2">Frequently Asked Questions</h1>
          <p className="text-neutral-600">Find answers to common questions about EsuSol</p>
        </div>

        <h2 className="text-xl font-bold text-neutral-900 mb-3">For Members</h2>
        <div className="space-y-4">
          {MEMBER_FAQS.map((faq, index) => renderFaqItem(faq, `member-${index}`))}
        </div>

        <h2 className="text-xl font-bold text-neutral-900 mt-8 mb-3">For Admins</h2>
        <div className="space-y-4">
          {ADMIN_FAQS.map((faq, index) => renderFaqItem(faq, `admin-${index}`))}
        </div>

        <div className="mt-8 bg-neutral-50 rounded-lg p-6 border border-neutral-200">
          <h3 className="font-semibold text-neutral-900 mb-1">Contact Support</h3>
          <p className="text-neutral-600 text-sm mb-4">Can't find what you're looking for? Send us a message.</p>
          <ContactForm />
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
